import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetPurchasePayments,
  CreatePurchasePayment,
  DeletePurchasePayment,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const purchasePaymentQueryKeys = {
  purchasePayments: (params?: Parameters<typeof GetPurchasePayments>[0]) =>
    ["purchasePayments", params] as const,
};

export const invalidatePurchasePaymentQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["purchasePayments"] });
};

export const usePurchasePaymentsQuery = (
  params: Parameters<typeof GetPurchasePayments>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: purchasePaymentQueryKeys.purchasePayments(params),
    queryFn: async () => unwrapApiResponse(await GetPurchasePayments(params)),
  });
};

export const usePurchasePaymentsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetPurchasePayments>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: purchasePaymentQueryKeys.purchasePayments(params),
        queryFn: async () =>
          unwrapApiResponse(await GetPurchasePayments(params)),
      }),
  });
};

export const useCreatePurchasePaymentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreatePurchasePayment>[0]) =>
      CreatePurchasePayment(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePurchasePaymentQueries(queryClient);
      }
    },
  });
};

export const useDeletePurchasePaymentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeletePurchasePayment(id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidatePurchasePaymentQueries(queryClient);
      }
    },
  });
};

export const usePurchasePaymentService = (config?: {
  fetchPurchasePaymentsParams?: Parameters<typeof GetPurchasePayments>[0];
  fetchPurchasePaymentsEnabled?: boolean;
}) => {
  const purchasePaymentsQuery = usePurchasePaymentsQuery(
    config?.fetchPurchasePaymentsParams!,
    {
      enabled:
        config?.fetchPurchasePaymentsEnabled !== false &&
        config?.fetchPurchasePaymentsParams !== undefined &&
        config?.fetchPurchasePaymentsParams !== null,
    },
  );

  const createPurchasePaymentMutation = useCreatePurchasePaymentMutation();
  const deletePurchasePaymentMutation = useDeletePurchasePaymentMutation();

  return {
    purchasePaymentsQuery,
    createPurchasePaymentMutation,
    deletePurchasePaymentMutation,
  };
};
